import { action, computed, makeAutoObservable } from "mobx";
import SalesEndpoints from "../api/SalesEndpoints";
import config from "../config";
import { ClientConfiguration } from "../models/ClientConfiguration";
import { SalesContext } from "../models/SalesContext";
import { EcommercePayment, OrderAddress } from "../requests/OrderRequests";
import {
    AcceptValidatePayment,
    LoadAcceptScript,
} from "../utils/acceptValidate";
import {
    LoadSongbirdScript,
    StartCardinalTransaction,
    UpdateJwt,
} from "../utils/cardinalSongbird";
import toast from "../utils/toast";
import AuthStore from "./AuthStore";
import { loadStripe, Stripe } from "@stripe/stripe-js";

class PaymentProcessingStore {
    private _currentCardinalTransaction: {
        resolve: (token: string) => void;
        reject: (err: any) => void;
    } | null = null;
    private _stripePromise: Promise<Stripe | null> | null = null;
    private _acceptLoading: boolean = false;
    private _cardinalLoading: boolean = false;
    private _squareLoading: boolean = false;
    private _config: ClientConfiguration = {
        gatewayId: undefined,
        squareApplicationId: "",
        squareLocationId: "",
        stripePublishableKey: "",
        cardinalConfigured: false,
        acceptHostedEnabled: false,
        acceptHostedApiLoginId: "",
        acceptHostedClientKey: "",
        currency: "CAD",
        clientId: -1,
    };

    constructor() {
        makeAutoObservable(this);
    }

    @action
    setClientConfig(config: ClientConfiguration) {
        this._config = config;
    }

    @action loadScripts(
        cartKey: string | undefined,
        discountCode: string | undefined,
    ) {
        if (!cartKey) return;
        if (this.cardinalConfigured) {
            this.loadSongbirdScript(cartKey, discountCode ?? null);
        }
        if (this.acceptEnabled) {
            this.loadAcceptScript();
        }
        // if (this.usingSquare) {
        //     this.loadSquareScript();
        // }

        if (this.usingStripe) {
            this.loadStripe();
        }
    }

    //TODO: Add some sort of prompt if script loading fails, like "Cannot securely process payments: please contact support."
    @action loadAcceptScript() {
        if (!this.acceptRequired()) {
            return;
        }

        this._acceptLoading = true;
        LoadAcceptScript(
            config.ACCEPT_SCRIPT_SRC || "",
            () => (this._acceptLoading = false),
            (prompt: string | false) => {
                // this.remoteLogging.error("An error occurred while loading the Accept.JS script: " + prompt);
                this._acceptLoading = false;
                // this.onFailure(prompt);
                toast.error(
                    prompt ||
                        "An error occurred while loading the payment processor.",
                );
            },
        );
    }

    @action async loadSongbirdScript(
        cartKey: string,
        discountCode: string | null,
    ) {
        if (!this.cardinalRequired()) {
            return;
        }

        try {
            this._cardinalLoading = true;
            const response = await SalesEndpoints.getCardinalToken(
                cartKey,
                discountCode || "",
            );
            LoadSongbirdScript(
                config.SONGBIRD_SCRIPT_SRC || "",
                // process.env.REACT_APP_ENVIRONMENT || "",
                "production",
                response.token,
                () => (this._cardinalLoading = false),
                this.cardinalResponseHandler,
                (prompt: string | false) => {
                    // this.remoteLogging.error("An error occurred while loading the Songbird script: " + prompt, cartKey);
                    this._cardinalLoading = false;
                    // this.onFailure(prompt);
                    toast.error(
                        prompt ||
                            "An error occurred while loading the payment processor.",
                    );
                },
            );
        } catch (err) {
            console.error("Could not load Songbird.js", err);
            this._cardinalLoading = false;
            if (
                err &&
                err.toString &&
                err.toString() === "This endpoint requires a non-empty cart."
            ) {
                // this.remoteLogging.error(
                //     "Could not load Songbird script, cart expired",
                //     cartKey,
                // );
                // this.onFailure(
                //     "Your cart has expired due to inactivity. Please refresh the page and try again.",
                // );
                toast.error(
                    "Your cart has expired due to inactivity. Please refresh the page and try again.",
                );
            } else {
                // this.remoteLogging.error(
                //     "Could not load Songbird script, an exception occurred: " +
                //         err,
                //     cartKey,
                // );
                // this.onFailure(false); // False triggers the default error message
                toast.error(
                    "An error occurred while loading payment processor.",
                );
            }
        }
    }

    // @action loadSquareScript() {
    //     if (!this.usingSquare) {
    //         return;
    //     }

    //     this._squareLoading = true;
    //     LoadSquareEcommerce(
    //         this.squareUrl,
    //         () => (this._squareLoading = false),
    //         err => {
    //             console.error("Error while loading Square", err);
    //             this._squareLoading = false;
    //             this.onFailure(false);
    //         }
    //     );
    // }

    @action loadStripe() {
        if (!this.usingStripe) {
            return;
        }

        this._stripePromise = loadStripe(this._config.stripePublishableKey);
    }

    @action async updateCart(cartKey: string, discountCode: string | null) {
        // TODO: If someone hammers on the + or - buttons, we could end up in the situation where the updated JWT is not the most recent.
        //       Should we add some sort of counter to check against?

        if (!this.cardinalRequired()) {
            return;
        }

        try {
            this._cardinalLoading = true;
            const response = await SalesEndpoints.getCardinalToken(
                cartKey,
                discountCode || "",
            );
            await UpdateJwt(response.token);
        } catch (err) {
            console.error("Could not update cart for Songbird.js");
            // this.remoteLogging.error("Could not update cart for Songbird.js, an exception occurred: " + err, cartKey);
            // this.onFailure(
            //     "Your cart has expired. Please refresh the page and try again.",
            // ); // This is the most likely outcome here
            toast.error("Your cart has exipred. Please refresh and try again.");
        } finally {
            this._cardinalLoading = false;
        }
    }

    @action async processPayment(
        orderNumber: string,
        orderAddress: OrderAddress,
        ecommercePayment: EcommercePayment,
    ): Promise<EcommercePayment> {
        try {
            // If Accept.JS is required, then we always start there. It will need to trigger Cardinal if necessary
            if (this.acceptRequired()) {
                const payment = await this.startAcceptFlow(
                    orderAddress,
                    ecommercePayment,
                );
                if (this.cardinalRequired()) {
                    const token = await this.startCardinalFlow(
                        orderNumber,
                        orderAddress,
                        ecommercePayment,
                    );
                    return Object.assign(
                        { cardinalResponseToken: token },
                        payment,
                    );
                } else {
                    return payment;
                }
            } else if (this.cardinalRequired()) {
                const token = await this.startCardinalFlow(
                    orderNumber,
                    orderAddress,
                    ecommercePayment,
                );
                return Object.assign(
                    { cardinalResponseToken: token },
                    ecommercePayment,
                );
            }

            // No special processing required
            return ecommercePayment;
        } finally {
            // KB April 26th: I don't like that we're setting these to true in functions that aren't responsible
            // for cleaning up after themselves, then delegating that management to the caller (makes it error prone
            // to reuse them). This logic should probably be refactored at some point in the future.
            this._cardinalLoading = false;
            this._acceptLoading = false;
        }
    }

    private startCardinalFlow = (
        orderNumber: string,
        orderAddress: OrderAddress,
        ecommercePayment: EcommercePayment,
    ): Promise<string> => {
        return new Promise((resolve, reject) => {
            this._cardinalLoading = true;
            this._currentCardinalTransaction = { resolve, reject };
            StartCardinalTransaction(
                orderNumber,
                orderAddress,
                ecommercePayment,
            ).catch((err) => reject(err));
        });
    };

    private cardinalResponseHandler = (
        success: boolean,
        prompt: string,
        token: string,
    ) => {
        if (!this._currentCardinalTransaction) {
            console.error(
                "In cardinalResponseHandler without an active promise. This should never happen.",
                prompt,
                token,
            );
            // this.remoteLogging.error("In cardinalResponseHandler without an active promise. This should never happen. Token: " + token);
            return;
        }

        if (success) {
            this._currentCardinalTransaction.resolve(token);
        } else {
            this._currentCardinalTransaction.reject(prompt);
        }
    };

    private startAcceptFlow = async (
        orderAddress: OrderAddress,
        ecommercePayment: EcommercePayment,
    ): Promise<EcommercePayment> => {
        this._acceptLoading = true;

        const loginId = this._config.acceptHostedApiLoginId;
        const clientKey = this._config.acceptHostedClientKey;
        const response = await AcceptValidatePayment(
            clientKey,
            loginId,
            orderAddress,
            ecommercePayment,
        );

        if (!response || response.messages.resultCode === "Error") {
            const msg = response.messages.message
                .map((message: any) => message.text)
                .join(" ");
            console.error("Accept.JS error: " + msg, response.messages);
            // this.remoteLogging.error("Accept.JS error: " + msg);
            throw new Error(msg);
        } else {
            const newEcommercePayment: EcommercePayment = Object.assign(
                {
                    acceptNonceDescriptor: response.opaqueData.dataDescriptor,
                    acceptNonceValue: response.opaqueData.dataValue,
                },
                ecommercePayment,
            );
            // Now that we have the nonce, we need to clear out everything else
            delete newEcommercePayment.cvv;
            delete newEcommercePayment.expiryMonth;
            delete newEcommercePayment.expiryYear;
            delete newEcommercePayment.nameOnCard;
            delete newEcommercePayment.cardNum;
            return newEcommercePayment;
        }
    };

    @computed get clientConfiguration() {
        return this._config;
    }

    @computed get usingSquare() {
        return this._config.gatewayId === 4 || this._config.gatewayId === 6;
    }

    @computed get usingStripe() {
        return this._config.gatewayId === 5;
    }

    @computed get usingCustomPaymentForm() {
        return this.usingSquare || this.usingStripe;
    }

    @computed get cardinalConfigured(): boolean {
        return this._config.cardinalConfigured;
    }

    @computed get acceptEnabled(): boolean {
        return this._config.acceptHostedEnabled;
    }

    @computed get refreshAdvised(): boolean {
        return this.usingSquare || this.cardinalConfigured;
    }

    @computed get loading(): boolean {
        return (
            this._cardinalLoading || this._acceptLoading || this._squareLoading
        );
    }

    @computed get gatewayId(): number | undefined {
        return this._config.gatewayId;
    }

    @computed get currency(): string {
        return this._config.currency ?? "CAD";
    }

    @computed get stripePromise(): Promise<Stripe | null> | null {
        return this._stripePromise;
    }

    private acceptRequired(): boolean {
        return (
            this.acceptEnabled &&
            AuthStore.salesContext === SalesContext.ECOMMERCE
        );
    }

    private cardinalRequired(): boolean {
        return (
            this.cardinalConfigured &&
            AuthStore.salesContext === SalesContext.ECOMMERCE
        );
    }
}

export default new PaymentProcessingStore();
